import { Box, Button, CircularProgress, styled } from "@material-ui/core";
import { navigate } from "gatsby";
import React, { useState } from "react";
import Imgix from "react-imgix";
import { formatPrice } from "../../../../helpers/price";
import { getDefaultProductVariant } from "../../../../helpers/product";
import { sleep } from "../../../../helpers/sleep";
import { getProductDetailsPath } from "../../../../helpers/url";
import useExpressDeliveryAvailability from "../../../../hooks/use-express-delivery-availability";
import useExpressDeliveryCart from "../../../../hooks/use-express-delivery-cart";
import useLocalizedLocationPath from "../../../../hooks/use-localized-location-path";
import useTranslate from "../../../../hooks/use-translate";
import useUnlocalizedLocationPath from "../../../../hooks/use-unlocalized-location-path";
import { ProductType } from "../../../../types/airgraft";
import ExpressDeliveryLogo from "../../../express-delivery/ExpressDeliveryLogo";

export const XpressButton = styled(Button)(({ theme }) => ({
  width: "100px",
  minHeight: "0px",
  height: "24px",
  backgroundColor: theme.palette.expressDelivery,
  color: theme.palette.common.white,
  fontSize: "1.2rem",
  "&:hover": {
    backgroundColor: theme.palette.expressDelivery
  },
  "& .MuiCircularProgress-root": {
    color: theme.palette.common.white
  },
  "&.Mui-disabled": {
    opacity: 0.75,
    fontSize: "1.1rem",
    color: theme.palette.grey[600],
    backgroundColor: theme.palette.grey[300],
    "&:hover": {
      backgroundColor: theme.palette.grey[300]
    }
  }
}));

export const LogoImgix = styled(Imgix)(({ theme }) => ({
  width: "auto",
  maxHeight: "35px",
  maxWidth: "120px"
}));

export function XpressProviderItem({ product }: { product: ProductType }) {
  const t = useTranslate();
  const pathname = useUnlocalizedLocationPath();
  const getLocalizedPath = useLocalizedLocationPath();
  const productVariant = getDefaultProductVariant(product);

  const {
    getProductExpressAvailability,
    isLoadingAvailableProducts,
    isLoadingAvailableProvider,
    toggleAvailabilityPanel,
    getAvailableProviderProductData
  } = useExpressDeliveryAvailability();

  const { toggleCartPanel, addItem } = useExpressDeliveryCart();

  const isLoadingProductAvailability =
    isLoadingAvailableProducts || isLoadingAvailableProvider;

  const productAvailability = getProductExpressAvailability(product);

  const providerProductData = getAvailableProviderProductData(product);

  const productPrice = providerProductData?.price || {
    amount: productVariant.price,
    currencyCode: "USD"
  };

  // UI state
  const [isAddingToCart, setAdding] = useState(false); // Button will show "Adding..." for few ms before adding to cart

  const handleXpressButtonClick = async () => {
    // Just redirect to PDP if clicked from homepage
    if (pathname === "/") {
      navigate(
        getLocalizedPath(
          getProductDetailsPath(product.productType, product.slug)
        )
      );
      return;
    }

    // Open address panel
    if (productAvailability !== "AVAILABLE") {
      toggleAvailabilityPanel(true);
      return;
    }

    // Add to cart
    setAdding(true);
    await sleep(400);
    setAdding(false);
    toggleCartPanel(true);
    addItem(
      {
        productVariantId: productVariant.originalId,
        metadata: {
          name: productVariant.title,
          slug: product.slug,
          productType: product.productType,
          brand: productVariant.brand.name,
          image: productVariant.featuredImage.url,
          thcGrams: productVariant.variantDetail?.coaReport?.weightGrams
        }
      },
      1
    );
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      bgcolor="grey.100"
      paddingX={{ xs: 2 }}
      paddingY={{ xs: 1 }}
      mb="1px"
      maxHeight="50px"
      height="50px"
    >
      <ExpressDeliveryLogo height="14px" />

      {/* Xpress button */}
      <XpressButton
        disabled={
          isLoadingProductAvailability ||
          isAddingToCart ||
          productAvailability === "SOLDOUT" ||
          productAvailability === "UNAVAILABLE"
        }
        onClick={handleXpressButtonClick}
        endIcon={
          !isLoadingProductAvailability &&
          !isAddingToCart &&
          (productAvailability === "AVAILABLE" ||
            productAvailability === "NO_ADDRESS") ? (
            <ExpressDeliveryLogo variant="truck" height="10px" />
          ) : null
        }
        data-testid="pdp-buy-options-xpress"
      >
        {/* Loading */}
        {isLoadingProductAvailability && (
          <CircularProgress size="10px" variant="indeterminate" />
        )}

        {/* Adding... */}
        {isAddingToCart && t("pdp.addingToCart")}

        {!isAddingToCart && !isLoadingProductAvailability && (
          <>
            {/* Available/No address: XPRESS button w/ price */}
            {(productAvailability === "AVAILABLE" ||
              productAvailability === "NO_ADDRESS") &&
              formatPrice(productPrice, { hideCurrencyCode: true })}

            {/* Unavailable: Disabled button */}
            {productAvailability === "UNAVAILABLE" && t("pdp.unavailable")}

            {/* Soldout: Disabled button */}
            {productAvailability === "SOLDOUT" && t("pdp.soldOut")}
          </>
        )}
      </XpressButton>
    </Box>
  );
}
